import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Transition } from "react-transition-group";
import { Animation } from "../../../../../components/Animation";
import Input from "../../../../../components/Form/Input";
import Button from "../../../../../components/Button";
import Icon from "../../../../../components/Icon";
import {
  ModalBackdrop,
  Modal,
  ModalDialog,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Title,
  Close,
  ModalAnimation,
  ErrorText,
} from "./styles";

import closeIcon from "../../../../../assets/icons/close_circled.svg";

interface Props {
  initialText?: string;
  initialUrl?: string;
  isEditing?: boolean;
  onInsert: (params: { text: string; url: string }) => void;
  onClose: () => void;
}

const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const InsertLinkModal = ({ initialText = "", initialUrl = "", isEditing = false, onInsert, onClose }: Props) => {
  const [text, setText] = useState(initialText || "");
  const [url, setUrl] = useState(initialUrl || "");
  const [error, setError] = useState("");

  // Si se abre con nuevos valores, los actualizamos
  useEffect(() => {
    setText(initialText || "");
    setUrl(initialUrl || "");
  }, [initialText, initialUrl]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Gestión de estilos del body/HTML cuando el modal está abierto
  useEffect(() => {
    // Aplicar estilos cuando el modal se monta
    document.body.style.height = "100%";
    document.body.style.overflow = "hidden";
    document.documentElement.style.height = "100%";
    document.documentElement.style.overflow = "hidden";

    // Limpiar estilos cuando el modal se desmonta
    return () => {
      document.body.style.height = "auto";
      document.body.style.overflow = "auto";
      document.documentElement.style.height = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, []);

  const handleBackdropClick = () => {
    onClose();
  };

  const handleInsert = () => {
    if (!text.trim()) {
      setError("Debes ingresar un texto");
      return;
    }
    if (!isValidUrl(url.trim())) {
      setError("Debes ingresar una URL válida (con https://)");
      return;
    }
    setError("");
    onInsert({ text: text.trim(), url: url.trim() });
    onClose();
  };

  const modalDOM = document.getElementById("modal") as HTMLElement;

  if (!modalDOM) {
    return null;
  }

  return ReactDOM.createPortal(
    <Transition in={true} timeout={350} mountOnEnter>
      {(state) => (
        <>
          <ModalAnimation
            name="fadeIn"
            className={`animation-${state}`}
          >
            <Modal>
              <ModalDialog width={420}>
                <ModalContent>
                  <ModalHeader>
                    <Close onClick={onClose}>
                      <Icon
                        icon={closeIcon}
                        size="24px"
                        color="black"
                      />
                    </Close>
                    <Title>{isEditing ? 'Editar link' : 'Insertar link'}</Title>
                  </ModalHeader>

                  <ModalBody>
                    <Input
                      name="linkText"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Texto visible (ej: Ver más)"
                      options={{
                        label: "Texto a mostrar",
                        size: "md",
                        skin: "base",
                        marginBottom: 12,
                      }}
                    />
                    <Input
                      name="linkUrl"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://example.com"
                      options={{
                        label: "URL del enlace",
                        size: "md",
                        skin: "base",
                        marginBottom: 12,
                      }}
                    />
                    {error && <ErrorText>{error}</ErrorText>}
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      type="button"
                      onClick={onClose}
                      options={{
                        type: "outline",
                        size: "md",
                        skin: "purple",
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      onClick={handleInsert}
                      options={{
                        type: "filled",
                        size: "md",
                        skin: "purple",
                      }}
                    >
                      {isEditing ? 'Guardar' : 'Insertar'} Link
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </ModalDialog>
            </Modal>
          </ModalAnimation>

          <Animation name="fadeIn" className={`animation-${state}`}>
            <ModalBackdrop onClick={handleBackdropClick} />
          </Animation>
        </>
      )}
    </Transition>,
    modalDOM
  );
};

export default InsertLinkModal;
